export const INITIAL_STATE = {
  // Wizard
  activeStep: 0,
  stepsCompleted: [false, false, false],

  // Paso 1: Upload
  upload: {
    files: [],
    isUploading: false,
    uploadProgress: 0,
    firebaseUrls: [],
  },

  // Datos procesados (compartido entre pasos 2 y 3)
  documents: [],
  currentDocumentId: null,

  // Paso 2: Validación de huertos
  orchardValidation: {
    filters: { search: '', status: 'all' },
    selectedRowIds: [],
    pagination: { page: 1, pageSize: 25 },
    sortModel: { createdAt: 'DESC' },
  },

  // Paso 3: Confirmación de cuarteles
  quarterConfirmation: {
    expandedOrchardIds: [],
    filters: { search: '', status: 'all' },
    pagination: { page: 1, pageSize: 20 },
  },

  // Modal de corrección
  modal: {
    open: false,
    editingQuarter: null,
    orchardContext: null,
  },

  // Resumen
  summary: {
    totalPdfs: 0,
    listo: 0,
    enRevision: 0,
    sinAsignar: 0,
  },

  // Pestaña 2: Visualización
  visualization: {
    documents: [],
    totalCount: 0,
    filters: {},
    pagination: { page: 1, pageSize: 25 },
    sortModel: { createdAt: 'DESC' },
  },

  // UI transversal
  ui: {
    loading: false,
    saving: false,
    error: null,
    snackbar: { open: false, message: '', severity: 'success' },
  },
};

export const laboratoryDocumentsReducer = (state, action) => {
  switch (action.type) {
    // ---- Wizard ----
    case 'SET_ACTIVE_STEP':
      return {
        ...state,
        activeStep: action.payload,
      };

    case 'SET_STEP_COMPLETED': {
      const newStepsCompleted = [...state.stepsCompleted];
      newStepsCompleted[action.payload.step] = action.payload.completed;
      return {
        ...state,
        stepsCompleted: newStepsCompleted,
      };
    }

    // ---- Upload (Paso 1) ----
    case 'SET_UPLOAD_FILES':
      return {
        ...state,
        upload: {
          ...state.upload,
          files: action.payload,
        },
      };

    case 'ADD_UPLOAD_FILES':
      return {
        ...state,
        upload: {
          ...state.upload,
          files: [...state.upload.files, ...action.payload],
        },
      };

    case 'REMOVE_UPLOAD_FILE':
      return {
        ...state,
        upload: {
          ...state.upload,
          files: state.upload.files.filter(
            (file) => file.id !== action.payload
          ),
        },
      };

    case 'SET_UPLOADING':
      return {
        ...state,
        upload: {
          ...state.upload,
          isUploading: action.payload,
        },
      };

    case 'SET_UPLOAD_PROGRESS':
      return {
        ...state,
        upload: {
          ...state.upload,
          uploadProgress: action.payload,
        },
      };

    case 'SET_FIREBASE_URLS':
      return {
        ...state,
        upload: {
          ...state.upload,
          firebaseUrls: action.payload,
        },
      };

    case 'CLEAR_UPLOAD':
      return {
        ...state,
        upload: INITIAL_STATE.upload,
      };

    // ---- Documents ----
    case 'SET_DOCUMENTS':
      return {
        ...state,
        documents: action.payload,
      };

    case 'UPDATE_DOCUMENT': {
      const updatedDocuments = state.documents.map((doc) =>
        doc.documentId === action.payload.documentId
          ? { ...doc, ...action.payload.data }
          : doc
      );
      return {
        ...state,
        documents: updatedDocuments,
      };
    }

    case 'SET_CURRENT_DOCUMENT_ID':
      return {
        ...state,
        currentDocumentId: action.payload,
      };

    // ---- Orchard Validation (Paso 2) ----
    case 'SET_ORCHARD_FILTERS':
      return {
        ...state,
        orchardValidation: {
          ...state.orchardValidation,
          filters: { ...state.orchardValidation.filters, ...action.payload },
        },
      };

    case 'SET_ORCHARD_SELECTED_ROWS':
      return {
        ...state,
        orchardValidation: {
          ...state.orchardValidation,
          selectedRowIds: action.payload,
        },
      };

    case 'SET_ORCHARD_PAGINATION':
      return {
        ...state,
        orchardValidation: {
          ...state.orchardValidation,
          pagination: { ...state.orchardValidation.pagination, ...action.payload },
        },
      };

    case 'ASSIGN_ORCHARD': {
      const { documentId, orchardData } = action.payload;
      const docs = state.documents.map((doc) =>
        doc.documentId === documentId
          ? { ...doc, ...orchardData }
          : doc
      );
      return {
        ...state,
        documents: docs,
      };
    }

    case 'BULK_ASSIGN_ORCHARD': {
      const { documentIds, orchardData } = action.payload;
      const docs = state.documents.map((doc) =>
        documentIds.includes(doc.documentId)
          ? { ...doc, ...orchardData }
          : doc
      );
      return {
        ...state,
        documents: docs,
      };
    }

    case 'ASSIGN_ORCHARD_BY_KEY': {
      const { documentId, orchardIndex, newOrchardData } = action.payload;
      const docs = state.documents.map((doc) => {
        if (doc.documentId !== documentId) return doc;
        return {
          ...doc,
          orchards: doc.orchards.map((orch, idx) => {
            if (idx !== orchardIndex) return orch;
            return { ...orch, ...newOrchardData };
          }),
        };
      });
      return { ...state, documents: docs };
    }

    case 'BULK_ASSIGN_ORCHARD_BY_KEY': {
      const { targets, newOrchardData } = action.payload;
      // targets: [{ documentId, orchardIndex }]
      const targetMap = {};
      targets.forEach((t) => {
        if (!targetMap[t.documentId]) targetMap[t.documentId] = new Set();
        targetMap[t.documentId].add(t.orchardIndex);
      });
      const docs = state.documents.map((doc) => {
        if (!targetMap[doc.documentId]) return doc;
        const orchardIndices = targetMap[doc.documentId];
        return {
          ...doc,
          orchards: doc.orchards.map((orch, idx) => {
            if (!orchardIndices.has(idx)) return orch;
            return { ...orch, ...newOrchardData };
          }),
        };
      });
      return { ...state, documents: docs };
    }

    // ---- Quarter Confirmation (Paso 3) ----
    case 'TOGGLE_ORCHARD_EXPAND': {
      const orchardId = action.payload;
      const isExpanded = state.quarterConfirmation.expandedOrchardIds.includes(orchardId);
      return {
        ...state,
        quarterConfirmation: {
          ...state.quarterConfirmation,
          expandedOrchardIds: isExpanded
            ? state.quarterConfirmation.expandedOrchardIds.filter((id) => id !== orchardId)
            : [...state.quarterConfirmation.expandedOrchardIds, orchardId],
        },
      };
    }

    case 'SET_QUARTER_FILTERS':
      return {
        ...state,
        quarterConfirmation: {
          ...state.quarterConfirmation,
          filters: { ...state.quarterConfirmation.filters, ...action.payload },
        },
      };

    case 'SET_QUARTER_PAGINATION':
      return {
        ...state,
        quarterConfirmation: {
          ...state.quarterConfirmation,
          pagination: { ...state.quarterConfirmation.pagination, ...action.payload },
        },
      };

    case 'UPDATE_QUARTER_VALIDATION': {
      const { documentId, orchardValue, ccValue, quarterValue, updatedData } = action.payload;
      const docs = state.documents.map((doc) => {
        if (doc.documentId !== documentId) return doc;
        return {
          ...doc,
          orchards: doc.orchards.map((orch) => {
            if (orch.orchardValue !== orchardValue) return orch;
            return {
              ...orch,
              cc: orch.cc.map((ccItem) => {
                if (ccItem.ccValue !== ccValue) return ccItem;
                return {
                  ...ccItem,
                  quarters: ccItem.quarters.map((q) => {
                    if (q.quarterValue !== quarterValue) return q;
                    return { ...q, ...updatedData };
                  }),
                };
              }),
            };
          }),
        };
      });
      return {
        ...state,
        documents: docs,
      };
    }

    // ---- Modal ----
    case 'OPEN_MODAL':
      return {
        ...state,
        modal: {
          open: true,
          editingQuarter: action.payload.editingQuarter,
          orchardContext: action.payload.orchardContext,
        },
      };

    case 'CLOSE_MODAL':
      return {
        ...state,
        modal: {
          open: false,
          editingQuarter: null,
          orchardContext: null,
        },
      };

    // ---- Summary ----
    case 'SET_SUMMARY':
      return {
        ...state,
        summary: action.payload,
      };

    // ---- Visualization (Pestaña 2) ----
    case 'SET_VISUALIZATION_DOCUMENTS':
      return {
        ...state,
        visualization: {
          ...state.visualization,
          documents: action.payload,
        },
      };

    case 'SET_VISUALIZATION_COUNT':
      return {
        ...state,
        visualization: {
          ...state.visualization,
          totalCount: action.payload,
        },
      };

    case 'SET_VISUALIZATION_FILTERS':
      return {
        ...state,
        visualization: {
          ...state.visualization,
          filters: { ...state.visualization.filters, ...action.payload },
        },
      };

    case 'SET_VISUALIZATION_PAGINATION':
      return {
        ...state,
        visualization: {
          ...state.visualization,
          pagination: { ...state.visualization.pagination, ...action.payload },
        },
      };

    // ---- UI ----
    case 'SET_LOADING':
      return {
        ...state,
        ui: { ...state.ui, loading: action.payload },
      };

    case 'SET_SAVING':
      return {
        ...state,
        ui: { ...state.ui, saving: action.payload },
      };

    case 'SET_ERROR':
      return {
        ...state,
        ui: { ...state.ui, error: action.payload },
      };

    case 'SET_SNACKBAR':
      return {
        ...state,
        ui: {
          ...state.ui,
          snackbar: { open: true, ...action.payload },
        },
      };

    case 'CLEAR_SNACKBAR':
      return {
        ...state,
        ui: {
          ...state.ui,
          snackbar: { open: false, message: '', severity: 'success' },
        },
      };

    // ---- Reset ----
    case 'RESET_STATE':
      return INITIAL_STATE;

    default:
      return state;
  }
};
